import type { components } from './generated/rest';

type Schemas = components['schemas'];

export type Session = Schemas['Session'];
export type User = Schemas['User'];
export type Me = Schemas['Me'];
export type Room = Schemas['Room'];
export type CreateRoomRequest = Schemas['CreateRoomRequest'];
export type PlayerSpec = Schemas['PlayerSpec'];
export type RoomMode = Schemas['RoomMode'];
export type GameMode = Schemas['GameMode'];
export type DeckBundleInfo = Schemas['DeckBundleInfo'];
export type GamePage = Schemas['GamePage'];
export type GameSummary = Schemas['GameSummary'];

/** Параметры `GET /games`. Пустой объект — первая страница с умолчаниями сервера. */
export interface GameQuery {
  limit?: number;
  cursor?: string;
  from?: string;
  to?: string;
  mode?: GameMode;
}
export type DeckBundle = Schemas['DeckBundle'];
export type Card = Schemas['Card'];

/**
 * ApiError — отказ сервера, разобранный по машинному коду.
 *
 * Решения принимаются по `code`, а не по `message`: формулировка последнего
 * предназначена человеку в логе, может измениться в любой момент и не
 * локализована (docs/auth-flow.md).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Единственный код, по которому вообще можно трогать сессию. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /**
   * Ручка предусмотрена контрактом, но не написана. Повторять нельзя,
   * функциональность прячется в UI.
   */
  get isNotImplemented(): boolean {
    return this.status === 501;
  }

  /**
   * Версия контракта несовместима с серверной. Чинится только обновлением
   * приложения, повтор бесполезен (`../clown-back/docs/auth-flow.md`).
   * Признак по статусу, а не по `code`: в 426 сервер шлёт `bad_request`,
   * тот же код, что и в обычной ошибке разбора.
   */
  get isOutdated(): boolean {
    return this.status === 426;
  }

  /** Зависимость сервера не сработала. Повтор с backoff, НЕ разлогинивать. */
  get isTransient(): boolean {
    return this.status === 503 || this.status >= 500;
  }
}

/**
 * Тупик: состояние, из которого приложение не выходит само.
 *
 * `session` — второй `401` подряд: `initData` протухла (Telegram не обновляет
 * её, пока Mini App открыто), и починит это только открытие заново.
 * `version` — `426`: клиент старше контракта.
 *
 * Оба обязаны показываться явным экраном, а не белым (`auth-flow.md`).
 */
export type SessionBlocker = 'session' | 'version';

/** Версия API в пути. Меняется на вызов, а не на весь клиент: v2-ручки поедут рядом с v1. */
export type ApiVersion = `v${number}`;

export const DefaultApiVersion: ApiVersion = 'v1';

export interface RestClientOptions {
  /** Хост без пути — `/api/<версия>` дописывает клиент (src/config.ts). */
  baseUrl: string;
  /** Версия по умолчанию для всех вызовов. Отдельный вызов может взять другую. */
  apiVersion?: ApiVersion;
  /** Сырая initData, ровно как её отдал Telegram: подпись считается по ней. */
  getInitData(): string;
  fetchImpl?: typeof fetch;
  /**
   * Приложение зашло в тупик. Вызывается из одного места — отсюда, — потому
   * что «второй 401 подряд» знает только тот, кто делал первый повтор:
   * экран на своём уровне отличить его от первого не может.
   */
  onBlocked?(reason: SessionBlocker): void;
}

/**
 * RestClient — сессия, комнаты, колоды.
 *
 * Токен живёт в памяти и только в ней: в Mini App повторный вход бесплатен,
 * initData доступна при каждом открытии. В localStorage токен не даёт выигрыша,
 * зато переживает выход из аккаунта (docs/auth-flow.md).
 */
export class RestClient {
  private token: string | null = null;
  private expiresAt: number | null = null;
  private pending: Promise<string> | null = null;
  private readonly fetchImpl: typeof fetch;

  constructor(private readonly options: RestClientOptions) {
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  /**
   * Действующий токен. `refresh` — после отказа `unauthorized`: тогда старый
   * заведомо мёртв и переиспользовать его нельзя.
   */
  async getToken({ refresh = false } = {}): Promise<string> {
    if (!refresh && this.token && !this.expiresSoon()) return this.token;
    this.pending ??= this.authenticate().finally(() => {
      this.pending = null;
    });
    return this.pending;
  }

  async me(): Promise<Me> {
    return this.request<Me>('GET', '/me');
  }

  async createRoom(body: CreateRoomRequest): Promise<Room> {
    return this.request<Room>('POST', '/rooms', body);
  }

  async getRoom(code: string): Promise<Room> {
    return this.request<Room>('GET', `/rooms/${encodeURIComponent(code)}`);
  }

  /**
   * Закрыть комнату, не имея живого сокета: партия помечается завершённой,
   * код освобождается, подключённые получают прощание. То же делает команда
   * `finish`, и из живой партии посылать надо именно её — эта ручка для
   * случая, когда сокет уже не поднять (хост свернул приложение, телефон
   * разрядился, а комната висит и держит код).
   *
   * 404 приходит и когда комнаты нет, и когда просящий не хост: код комнаты
   * — четыре символа, и 403 подтверждал бы, что такая существует
   * (docs/backend-contract-changelog.md, шаг Н4).
   */
  async closeRoom(code: string): Promise<void> {
    return this.request<void>('DELETE', `/rooms/${encodeURIComponent(code)}`);
  }

  /**
   * История партий страницами. Курсор непрозрачен: клиент его не собирает и
   * не разбирает, а возвращает серверу тем же, каким получил в `nextCursor`.
   */
  async listGames(query: GameQuery = {}): Promise<GamePage> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined) params.set(key, String(value));
    }
    const search = params.toString();
    return this.request<GamePage>('GET', `/games${search ? `?${search}` : ''}`);
  }

  async listDecks(): Promise<DeckBundleInfo[]> {
    return this.request<DeckBundleInfo[]>('GET', '/decks');
  }

  /**
   * Бандл целиком: версия, мини-игры и все карточки. 402 контрактом объявлен,
   * но пока биллинг заморожен, недостижим — обрабатывается общим путём ApiError,
   * отдельной ветки под него нет (../clown-back/api/openapi.yaml).
   */
  async getDeckBundle(version: string): Promise<DeckBundle> {
    return this.request<DeckBundle>('GET', `/decks/${encodeURIComponent(version)}`);
  }

  async logout(): Promise<void> {
    try {
      await this.request<void>('DELETE', '/auth/session');
    } finally {
      this.forget();
    }
  }

  forget(): void {
    this.token = null;
    this.expiresAt = null;
  }

  // ── внутреннее ────────────────────────────────────────────────────────────

  private expiresSoon(): boolean {
    if (this.expiresAt == null) return false;
    // Обновляемся за сутки до истечения, а не по первому 401: initData в
    // открытом больше суток приложении уже не обновится.
    return this.expiresAt - Date.now() < 24 * 60 * 60 * 1000;
  }

  private async authenticate(): Promise<string> {
    // Вход всегда идёт по версии по умолчанию: сессия одна на клиента, и
    // выпускать её заново ради v2-ручки незачем.
    const url = this.url('/auth/telegram', this.options.apiVersion ?? DefaultApiVersion);
    const session = await this.call<Session>('POST', url, {
      initData: this.options.getInitData(),
    });
    this.token = session.token;
    this.expiresAt = Date.parse(session.expiresAt);
    return session.token;
  }

  /**
   * Запрос под сессией. Один молчаливый повторный вход на 401 — пользователь
   * ничего не замечает. Второй 401 подряд означает протухшую initData, и его
   * обязан обработать экран, а не ещё один повтор.
   */
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    version: ApiVersion = this.options.apiVersion ?? DefaultApiVersion,
  ): Promise<T> {
    const url = this.url(path, version);
    try {
      const token = await this.getToken();
      try {
        return await this.call<T>(method, url, body, token);
      } catch (err) {
        if (!(err instanceof ApiError) || !err.isUnauthorized) throw err;
        const fresh = await this.getToken({ refresh: true });
        return await this.call<T>(method, url, body, fresh);
      }
    } catch (err) {
      // Сюда приходит и отказ самого входа: `getToken` ходит в
      // POST /auth/telegram, и его 401 значит ровно то же — initData мертва.
      this.reportBlocker(err);
      throw err;
    }
  }

  /**
   * Тупик отличается от обычного отказа тем, что до этой точки уже был ровно
   * один молчаливый повторный вход: до `request` доходит только то, что им
   * не лечится.
   */
  private reportBlocker(err: unknown): void {
    if (!(err instanceof ApiError)) return;
    if (err.isOutdated) this.options.onBlocked?.('version');
    else if (err.isUnauthorized) this.options.onBlocked?.('session');
  }

  /**
   * Полный адрес ручки. Версия живёт здесь, а не в переменной окружения:
   * с ней в адресе перевести один вызов на v2 нельзя, не переведя все сразу.
   */
  private url(path: string, version: ApiVersion): string {
    return `${this.options.baseUrl}/api/${version}${path}`;
  }

  private async call<T>(
    method: string,
    url: string,
    body?: unknown,
    token?: string,
  ): Promise<T> {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['Content-Type'] = 'application/json';
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const response = await this.fetchImpl(url, {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });

    if (!response.ok) throw await toApiError(response);
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = 'internal';
  let message = response.statusText;
  try {
    const body = (await response.json()) as { code?: string; message?: string };
    if (body.code) code = body.code;
    if (body.message) message = body.message;
  } catch {
    // Тело не разобралось — статуса достаточно, решение принимается по нему.
  }
  return new ApiError(response.status, code, message);
}
